import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  passosDoImpacto,
  PassoImpactoTipologias,
  PassoImpactoSistemistas,
} from "./AvisosImpactoN1";
import { IImpactoN1 } from "../../services/materiaisN1";

interface ModalConfirmacaoHomologarProps {
  open: boolean;
  onClose: () => void;
  onConfirmar: () => Promise<void>;
  quantidade: number;
  codigosProdutos: string[];
  isLoading?: boolean;
  /** Itens A4-A6 (02/09): avisos de impacto viram passos ANTES da confirmação. */
  impacto?: IImpactoN1 | null;
}

export const ModalConfirmacaoHomologar: React.FC<ModalConfirmacaoHomologarProps> = ({
  open,
  onClose,
  onConfirmar,
  quantidade,
  codigosProdutos,
  isLoading = false,
  impacto,
}) => {
  // Assistente (itens A4-A6): passos de aviso primeiro; a confirmação original é o último passo.
  const passos = passosDoImpacto(impacto);
  const [indicePasso, setIndicePasso] = useState(0);
  useEffect(() => {
    if (open) setIndicePasso(0);
  }, [open]);
  const passoAtual = indicePasso < passos.length ? passos[indicePasso] : "confirmacao";

  const handleConfirmar = async () => {
    await onConfirmar();
    onClose();
  };

  // FASE 05 — 2ª validação/PO 2026-07-24 (6.2): apresentar a lista completa (rolável).
  const codigosExibidos = codigosProdutos;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          // Itens A4-A6: largo o bastante para a tabela de tipologias não rolar na horizontal;
          // sem avisos, mantém o tamanho compacto de sempre.
          maxWidth: passoAtual === "confirmacao" ? "400px" : "900px",
        },
      }}
    >
      <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600, pb: 1 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6">Homologar produtos</Typography>
          </Grid>
          <Grid item>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: "#3C90AD" }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      {passoAtual === "tipologias" && (
        <PassoImpactoTipologias
          impacto={impacto!}
          onCancelar={onClose}
          onContinuar={() => setIndicePasso((p) => p + 1)}
        />
      )}

      {passoAtual === "sistemistas" && (
        <PassoImpactoSistemistas
          impacto={impacto!}
          onCancelar={onClose}
          onContinuar={() => setIndicePasso((p) => p + 1)}
        />
      )}

      {passoAtual === "confirmacao" && (
        <>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Os produtos com status Em Edição ficarão com status Em Homologação para a validação
              no CEM. Após a validação, acesse o MOF &gt; Produtos e acione o botão Concluir
              Homologação. <strong>Deseja continuar?</strong> ({quantidade} produto(s))
            </DialogContentText>

            {codigosExibidos.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Produtos:
                </Typography>
                <List dense sx={{ maxHeight: 150, overflow: "auto", backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  {codigosExibidos.map((codigo, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={codigo} primaryTypographyProps={{ variant: "body2" }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              Esta ação moverá os produtos para "Em homologação".
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} variant="outlined" sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}>
              CANCELAR
            </Button>
            <Button
              onClick={handleConfirmar}
              variant="contained"
              disabled={isLoading}
              sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
            >
              {isLoading ? "HOMOLOGANDO..." : "HOMOLOGAR"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
